import * as React from 'react';
import classNames from 'classnames';

import { Button } from 'components/Button';

import IntroColumn, { IntroColumnVariant } from 'tempcontrol/components/intro-column';
import WizardHero from 'tempcontrol/components/wizard-hero';
import { WizardStepId } from 'tempcontrol/enums';

export type WizardContentMaxWidth = '400' | '600';

interface WizardLayoutProps {
  currentStep: WizardStepId;
  /** Heading for the left intro column (e.g. "Project configuration"). */
  introTitle: string;
  /** Body paragraph for the left intro column. */
  introBody?: string;
  /** Optional callout / tooltip slot under the intro body. */
  introCallout?: React.ReactNode;
  /** Left-column layout variant — s1 uses `intro`, steps 2–4 use `step`. */
  introVariant?: IntroColumnVariant;
  /** Right form-column body. */
  children: React.ReactNode;
  /** Footer slot - typically a Next/Save button. */
  footer?: React.ReactNode;
  /** Optional banner/error slot rendered above the form content. */
  banner?: React.ReactNode;
  /** "Back" handler wired into the left column - omit for Step 1. */
  onBack?: () => void;
  /** Optional right slot inside the persistent hero (e.g. "New project"). */
  heroRightSlot?: React.ReactNode;
  /** Max width utility on the right column inner wrapper. */
  contentMaxWidth?: WizardContentMaxWidth;
  /** Duplicate of intro callout rendered below the row on mobile (s1 history). */
  mobileCallout?: React.ReactNode;
}

const contentMaxWidthClass: Record<WizardContentMaxWidth, string> = {
  '400': 'wmax-400@md',
  '600': 'wmax-600@md',
};

/**
 * Persistent wizard chrome.
 *
 * Layout follows the design:
 *   - Always-visible hero (eyebrow + title + body + dot pattern)
 *   - Two-column body: left intro column (BACK + STEP X/Y + heading + body)
 *     and right form column with the live step content + footer
 */
export const WizardLayout: React.VFC<WizardLayoutProps> = ({
  currentStep,
  introTitle,
  introBody,
  introCallout,
  introVariant = 'step',
  children,
  footer,
  banner,
  onBack,
  heroRightSlot,
  contentMaxWidth = '600',
  mobileCallout,
}) => {
  return (
    <>
      <WizardHero rightSlot={heroRightSlot} />

      <section className="c-block c-block--spacing-t-extra-small c-block--spacing-t@lg c-block--spacing-b-small c-block--spacing-b@lg u-overflow-hidden">
        <div className="o-container-fluid">
          <div className="o-row u-relative">
            <div className="o-col-12 o-col-5@md o-col-4@lg u-pr-0@xl u-static u-relative@md">
              <IntroColumn
                currentStep={currentStep}
                onBack={onBack}
                title={introTitle}
                body={introBody}
                callout={introCallout}
                variant={introVariant}
              />
            </div>

            <div className="o-col-12 o-col-7@md o-col-8@lg u-pl-spacer-section-small@xl">
              <div className={classNames(contentMaxWidthClass[contentMaxWidth], 'u-mx-auto@md')}>
                {banner ? <div className="u-mb-spacer-base">{banner}</div> : null}

                {children}

                {typeof footer !== 'undefined' && <div className="u-mt-spacer-base-large">{footer}</div>}
              </div>
            </div>

            {typeof mobileCallout !== 'undefined' && <div className="o-col-12 u-hidden@md">{mobileCallout}</div>}
          </div>
        </div>
      </section>
    </>
  );
};

interface WizardFooterProps {
  onNext: () => void;
  isSubmitting?: boolean;
  nextLabel?: string;
  rightExtras?: React.ReactNode;
  isLarge?: boolean;
}

/**
 * Footer for wizard steps. Designs only show a single forward CTA below
 * the form (Back lives in the intro column), so we no longer split into
 * a Back/Next row.
 */
export const WizardFooter: React.VFC<WizardFooterProps> = ({
  onNext,
  isSubmitting,
  nextLabel = 'Next',
  rightExtras,
  isLarge,
}) => {
  return (
    <div className="u-flex u-items-center u-flex-wrap" style={{ gap: '0.75rem' }}>
      <Button
        variant="primary"
        onClick={onNext}
        isLoading={isSubmitting}
        rightIcon="arrow"
        className={isLarge ? 'c-button--large' : undefined}
      >
        {nextLabel}
      </Button>
      {rightExtras}
    </div>
  );
};

export default WizardLayout;
